import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isGridArrowKey, moveGridSelection } from '../../shared/grid/navigation';
import { HintButton } from '../../shared/grid/HintButton';
import type { GameComponentProps } from '../core/types';
import {
  clueCells,
  cluesForCell,
  crosswordWrongCells,
  firstOpenCell,
  isCrosswordSolved,
} from './logic';
import {
  isMiniCrosswordPuzzle,
  type CrosswordClue,
  type CrosswordDirection,
  type MiniCrosswordPuzzle,
} from './types';

type CrosswordSavedState = {
  version: 1;
  entries: string[];
  selectedIndex: number;
  direction: CrosswordDirection;
  checkUsed: boolean;
  revealCount: number;
  wrongCells: number[];
};

function readSavedState(value: unknown, puzzle: MiniCrosswordPuzzle): CrosswordSavedState {
  const fallback: CrosswordSavedState = {
    version: 1,
    entries: puzzle.solution.map((cell) => (cell === '#' ? '#' : '')),
    selectedIndex: firstOpenCell(puzzle.blocks),
    direction: 'across',
    checkUsed: false,
    revealCount: 0,
    wrongCells: [],
  };
  if (!value || typeof value !== 'object') return fallback;
  const candidate = value as Partial<CrosswordSavedState>;
  const entries =
    Array.isArray(candidate.entries) && candidate.entries.length === puzzle.solution.length
      ? candidate.entries.map((letter, index) =>
          puzzle.blocks[index]
            ? '#'
            : typeof letter === 'string' && /^[A-Z]$/.test(letter)
              ? letter
              : '',
        )
      : fallback.entries;
  const selectedIndex =
    Number.isInteger(candidate.selectedIndex) &&
    candidate.selectedIndex! >= 0 &&
    candidate.selectedIndex! < puzzle.solution.length &&
    !puzzle.blocks[candidate.selectedIndex!]
      ? candidate.selectedIndex!
      : fallback.selectedIndex;
  return {
    version: 1,
    entries,
    selectedIndex,
    direction: candidate.direction === 'down' ? 'down' : 'across',
    checkUsed: candidate.checkUsed === true,
    revealCount:
      Number.isInteger(candidate.revealCount) && candidate.revealCount! >= 0
        ? candidate.revealCount!
        : 0,
    wrongCells: Array.isArray(candidate.wrongCells)
      ? candidate.wrongCells.filter(
          (index): index is number =>
            Number.isInteger(index) && index >= 0 && index < puzzle.solution.length,
        )
      : [],
  };
}

function activeClueFor(
  puzzle: MiniCrosswordPuzzle,
  index: number,
  direction: CrosswordDirection,
): CrosswordClue {
  const crossing = cluesForCell(puzzle.clues, puzzle.size, index);
  return crossing.find((clue) => clue.direction === direction) ?? crossing[0] ?? puzzle.clues[0];
}

function orderClues(clues: readonly CrosswordClue[]): CrosswordClue[] {
  return [...clues].sort((left, right) => {
    if (left.direction !== right.direction) return left.direction === 'across' ? -1 : 1;
    return left.number - right.number || left.start - right.start;
  });
}

export function MiniCrosswordGame({
  puzzle,
  savedState,
  disabled,
  onStateChange,
  onCharge,
  onComplete,
}: GameComponentProps) {
  if (!isMiniCrosswordPuzzle(puzzle.payload)) {
    throw new Error('Mini Crossword received an invalid puzzle payload.');
  }
  const crossword = puzzle.payload;
  const initial = useMemo(() => readSavedState(savedState, crossword), [crossword, savedState]);
  const [entries, setEntries] = useState(initial.entries);
  const [selectedIndex, setSelectedIndex] = useState(initial.selectedIndex);
  const [direction, setDirection] = useState(initial.direction);
  const [checkUsed, setCheckUsed] = useState(initial.checkUsed);
  const [revealCount, setRevealCount] = useState(initial.revealCount);
  const [wrongCells, setWrongCells] = useState(new Set(initial.wrongCells));
  const [status, setStatus] = useState('');
  const gridRef = useRef<HTMLDivElement>(null);
  const keyboardInputRef = useRef<HTMLInputElement>(null);
  const completionSent = useRef(false);

  const activeClue = useMemo(
    () => activeClueFor(crossword, selectedIndex, direction),
    [crossword, direction, selectedIndex],
  );
  const orderedClues = useMemo(() => orderClues(crossword.clues), [crossword.clues]);
  const activeCells = useMemo(
    () => new Set(clueCells(activeClue, crossword.size)),
    [activeClue, crossword.size],
  );
  const numberByCell = useMemo(
    () => new Map(crossword.clues.map((clue) => [clue.start, clue.number])),
    [crossword.clues],
  );

  const persist = useCallback(
    (
      nextEntries: string[],
      nextIndex: number,
      nextDirection: CrosswordDirection,
      nextCheckUsed: boolean,
      nextRevealCount: number,
      nextWrong: Set<number>,
    ) => {
      onStateChange({
        version: 1,
        entries: nextEntries,
        selectedIndex: nextIndex,
        direction: nextDirection,
        checkUsed: nextCheckUsed,
        revealCount: nextRevealCount,
        wrongCells: [...nextWrong],
      });
    },
    [onStateChange],
  );

  const focusCell = (index: number) =>
    window.requestAnimationFrame(() =>
      gridRef.current?.querySelector<HTMLElement>(`[data-grid-index="${index}"]`)?.focus(),
    );

  const openMobileKeyboard = () => {
    keyboardInputRef.current?.focus({ preventScroll: true });
  };

  const select = useCallback(
    (index: number, focus = false, requestedDirection?: CrosswordDirection) => {
      if (crossword.blocks[index]) return;
      const crossing = cluesForCell(crossword.clues, crossword.size, index);
      let nextDirection = requestedDirection ?? direction;
      if (index === selectedIndex && crossing.length === 2 && !requestedDirection) {
        nextDirection = direction === 'across' ? 'down' : 'across';
      } else if (!crossing.some((clue) => clue.direction === nextDirection)) {
        nextDirection = crossing[0]?.direction ?? direction;
      }
      setSelectedIndex(index);
      setDirection(nextDirection);
      persist(entries, index, nextDirection, checkUsed, revealCount, wrongCells);
      if (focus) focusCell(index);
    },
    [
      checkUsed,
      crossword.blocks,
      crossword.clues,
      crossword.size,
      direction,
      entries,
      persist,
      revealCount,
      selectedIndex,
      wrongCells,
    ],
  );

  const moveWithinClue = useCallback(
    (offset: number, from = selectedIndex): number => {
      const clue = activeClueFor(crossword, from, direction);
      const cells = clueCells(clue, crossword.size);
      const position = cells.indexOf(from);
      return cells[Math.max(0, Math.min(cells.length - 1, position + offset))];
    },
    [crossword, direction, selectedIndex],
  );

  const advanceAfterEntry = useCallback(
    (nextEntries: readonly string[]): { index: number; direction: CrosswordDirection } => {
      const currentCells = clueCells(activeClue, crossword.size);
      const position = currentCells.indexOf(selectedIndex);
      const nextOpenInWord = currentCells
        .slice(position + 1)
        .find((cell) => nextEntries[cell] === '');
      if (nextOpenInWord !== undefined) {
        return { index: nextOpenInWord, direction: activeClue.direction };
      }
      const wrappedOpenInWord = currentCells.find((cell) => nextEntries[cell] === '');
      if (wrappedOpenInWord !== undefined) {
        return { index: wrappedOpenInWord, direction: activeClue.direction };
      }
      const currentClueIndex = orderedClues.findIndex((clue) => clue.id === activeClue.id);
      for (let offset = 1; offset < orderedClues.length; offset += 1) {
        const clue = orderedClues[(currentClueIndex + offset) % orderedClues.length];
        const openCell = clueCells(clue, crossword.size).find((cell) => nextEntries[cell] === '');
        if (openCell !== undefined) return { index: openCell, direction: clue.direction };
      }
      return { index: selectedIndex, direction };
    },
    [activeClue, crossword.size, direction, orderedClues, selectedIndex],
  );

  const enterLetter = useCallback(
    (letter: string) => {
      if (disabled || crossword.blocks[selectedIndex]) return;
      const nextEntries = [...entries];
      nextEntries[selectedIndex] = letter;
      const nextWrong = new Set(wrongCells);
      nextWrong.delete(selectedIndex);
      const nextTarget = letter
        ? advanceAfterEntry(nextEntries)
        : { index: selectedIndex, direction };
      setEntries(nextEntries);
      setWrongCells(nextWrong);
      setSelectedIndex(nextTarget.index);
      setDirection(nextTarget.direction);
      setStatus(letter ? `${letter} entered` : 'Letter cleared');
      persist(
        nextEntries,
        nextTarget.index,
        nextTarget.direction,
        checkUsed,
        revealCount,
        nextWrong,
      );
    },
    [
      checkUsed,
      crossword.blocks,
      advanceAfterEntry,
      direction,
      disabled,
      entries,
      persist,
      revealCount,
      selectedIndex,
      wrongCells,
    ],
  );

  const backspace = useCallback(() => {
    if (entries[selectedIndex]) {
      enterLetter('');
      return;
    }
    const previous = moveWithinClue(-1);
    const nextEntries = [...entries];
    nextEntries[previous] = '';
    const nextWrong = new Set(wrongCells);
    nextWrong.delete(previous);
    setEntries(nextEntries);
    setSelectedIndex(previous);
    setWrongCells(nextWrong);
    setStatus('Previous letter cleared');
    persist(nextEntries, previous, direction, checkUsed, revealCount, nextWrong);
  }, [
    checkUsed,
    direction,
    enterLetter,
    entries,
    moveWithinClue,
    persist,
    revealCount,
    selectedIndex,
    wrongCells,
  ]);

  const changeClue = useCallback(
    (offset: number, focusGrid = true) => {
      const current = orderedClues.findIndex((clue) => clue.id === activeClue.id);
      const step = offset < 0 ? -1 : 1;
      const immediateNext =
        orderedClues[(current + step + orderedClues.length) % orderedClues.length];
      for (let distance = 1; distance <= orderedClues.length; distance += 1) {
        const clue =
          orderedClues[
            (current + step * distance + orderedClues.length * distance) % orderedClues.length
          ];
        const firstOpen = clueCells(clue, crossword.size).find((cell) => entries[cell] === '');
        if (firstOpen !== undefined) {
          select(firstOpen, focusGrid, clue.direction);
          return;
        }
      }
      select(immediateNext.start, focusGrid, immediateNext.direction);
    },
    [activeClue.id, crossword.size, entries, orderedClues, select],
  );

  const checkGrid = useCallback(() => {
    if (disabled) return;
    const nextWrong = new Set(crosswordWrongCells(entries, crossword.solution));
    if (!checkUsed) onCharge('check-grid', 30_000, 'Check & highlight');
    setCheckUsed(true);
    setWrongCells(nextWrong);
    setStatus(
      nextWrong.size === 0
        ? 'No incorrect entered letters'
        : `${nextWrong.size} incorrect ${nextWrong.size === 1 ? 'letter' : 'letters'} highlighted${
            checkUsed ? '' : ' · +30s'
          }`,
    );
    persist(entries, selectedIndex, direction, true, revealCount, nextWrong);
  }, [
    checkUsed,
    crossword.solution,
    direction,
    disabled,
    entries,
    onCharge,
    persist,
    revealCount,
    selectedIndex,
  ]);

  const revealLetter = useCallback(() => {
    if (disabled) return;
    const nextEntries = [...entries];
    nextEntries[selectedIndex] = crossword.solution[selectedIndex];
    const nextWrong = new Set(wrongCells);
    nextWrong.delete(selectedIndex);
    const nextCount = revealCount + 1;
    const nextTarget = advanceAfterEntry(nextEntries);
    onCharge(`reveal-letter-${nextCount}`, 8_000, 'Reveal letter');
    setEntries(nextEntries);
    setWrongCells(nextWrong);
    setRevealCount(nextCount);
    setSelectedIndex(nextTarget.index);
    setDirection(nextTarget.direction);
    setStatus('Letter revealed · +8s');
    persist(nextEntries, nextTarget.index, nextTarget.direction, checkUsed, nextCount, nextWrong);
  }, [
    checkUsed,
    advanceAfterEntry,
    crossword.solution,
    disabled,
    entries,
    onCharge,
    persist,
    revealCount,
    selectedIndex,
    wrongCells,
  ]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (disabled || event.metaKey || event.ctrlKey || event.altKey) return;
      if (/^[a-zA-Z]$/.test(event.key)) {
        event.preventDefault();
        enterLetter(event.key.toUpperCase());
      } else if (event.key === 'Backspace' || event.key === 'Delete') {
        event.preventDefault();
        backspace();
      } else if (event.key === 'Tab') {
        event.preventDefault();
        changeClue(event.shiftKey ? -1 : 1);
      } else if (isGridArrowKey(event.key)) {
        event.preventDefault();
        const next = moveGridSelection(selectedIndex, event.key, crossword.size, crossword.size);
        if (!crossword.blocks[next]) select(next, true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    backspace,
    changeClue,
    crossword.blocks,
    crossword.size,
    disabled,
    enterLetter,
    select,
    selectedIndex,
  ]);

  useEffect(() => {
    if (completionSent.current || !isCrosswordSolved(entries, crossword.solution)) return;
    completionSent.current = true;
    setStatus('Crossword complete!');
    onComplete({
      kind: 'solved',
      baseDeltaMs: 0,
      message: 'Crossword complete!',
      metadata: { checkUsed, revealCount },
    });
  }, [checkUsed, crossword.solution, entries, onComplete, revealCount]);

  useEffect(() => {
    if (isCrosswordSolved(entries, crossword.solution)) return;
    const openEntries = entries.filter((entry) => entry !== '#');
    if (openEntries.some((entry) => entry === '')) return;
    setStatus('Grid filled · use Check & highlight to submit');
  }, [crossword.solution, entries]);

  return (
    <section className="mini-crossword-game" aria-labelledby="mini-crossword-title">
      <p className="eyebrow">
        {crossword.size} × {crossword.size} · across &amp; down
      </p>
      <h1 id="mini-crossword-title" className="sr-only">
        Mini Crossword
      </h1>
      <div
        ref={gridRef}
        className="crossword-grid"
        style={{ gridTemplateColumns: `repeat(${crossword.size}, var(--crossword-cell))` }}
        role="grid"
        aria-label={`Mini Crossword grid, ${crossword.size} rows by ${crossword.size} columns`}
      >
        {entries.map((letter, index) => {
          if (crossword.blocks[index]) {
            return (
              <span
                key={index}
                className="crossword-cell crossword-cell--block"
                aria-hidden="true"
              />
            );
          }
          const row = Math.floor(index / crossword.size);
          const column = index % crossword.size;
          return (
            <button
              key={index}
              type="button"
              role="gridcell"
              data-grid-index={index}
              tabIndex={selectedIndex === index ? 0 : -1}
              aria-selected={selectedIndex === index}
              aria-invalid={wrongCells.has(index) || undefined}
              aria-label={`Row ${row + 1} column ${column + 1}, ${letter || 'empty'}${wrongCells.has(index) ? ', incorrect' : ''}`}
              className={`crossword-cell ${activeCells.has(index) ? 'crossword-cell--active' : ''} ${
                selectedIndex === index ? 'crossword-cell--selected' : ''
              } ${wrongCells.has(index) ? 'crossword-cell--wrong' : ''}`}
              onClick={() => {
                select(index);
                openMobileKeyboard();
              }}
            >
              {numberByCell.has(index) && <small>{numberByCell.get(index)}</small>}
              <strong>{letter}</strong>
            </button>
          );
        })}
      </div>
      <input
        ref={keyboardInputRef}
        className="crossword-keyboard-input"
        aria-label="Crossword keyboard input"
        type="text"
        inputMode="text"
        autoCapitalize="characters"
        autoComplete="off"
        spellCheck={false}
        autoFocus={!disabled}
        disabled={disabled}
        value=""
        onChange={(event) => {
          const letter = event.currentTarget.value.toUpperCase().match(/[A-Z]/g)?.at(-1);
          if (letter) enterLetter(letter);
        }}
        onKeyDown={(event) => {
          event.stopPropagation();
          if (event.key === 'Backspace' || event.key === 'Delete') {
            event.preventDefault();
            backspace();
          } else if (event.key === 'Tab') {
            event.preventDefault();
            changeClue(event.shiftKey ? -1 : 1);
          }
        }}
      />
      <button
        type="button"
        className="crossword-keyboard-trigger"
        disabled={disabled}
        onClick={openMobileKeyboard}
      >
        <span aria-hidden="true">⌨</span> Open keyboard
      </button>

      <div className="crossword-clue-card" aria-live="polite">
        <button
          type="button"
          onClick={() => {
            changeClue(-1, false);
            openMobileKeyboard();
          }}
          disabled={disabled}
          aria-label="Previous clue"
        >
          ←
        </button>
        <div>
          <span>
            {activeClue.number} {activeClue.direction === 'across' ? 'Across' : 'Down'}
          </span>
          <strong>{activeClue.clue}</strong>
        </div>
        <button
          type="button"
          onClick={() => {
            changeClue(1, false);
            openMobileKeyboard();
          }}
          disabled={disabled}
          aria-label="Next clue"
        >
          →
        </button>
      </div>
      <div className="crossword-status" role="status" aria-live="polite">
        {status}
      </div>
      <div className="grid-actions">
        <HintButton
          label="Check & highlight"
          costSeconds={30}
          paid={checkUsed}
          disabled={disabled}
          onClick={checkGrid}
        />
        <button
          type="button"
          className="grid-action"
          disabled={disabled}
          onClick={revealLetter}
          aria-label="Reveal selected letter, costs 8 seconds each time"
        >
          Reveal letter <span>(+8s)</span>
        </button>
      </div>
    </section>
  );
}
